/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
//#region imports
import { Component, OnInit, HostBinding, HostListener, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Message } from 'primeng/api';
import { Auth } from 'aws-amplify';
import { Router } from '@angular/router';
import { AppSettingService } from 'src/app/app-setting.service';
import { CognitoUserSession } from 'src/app/components/authentication/cognito-model/cognito-user.model';
import { VMOnlineLoginService } from '../login/vmonline-login.service';
import copy from 'copy-to-clipboard';
import { LocalRoutes } from 'src/app/local-routes';
import { AuthService } from '../../../auth/auth.service';
import * as errorCodes from 'src/app/error-codes';
import { MessageComponent } from '../../controls/message/message.component';

import { QrCodeComponent } from 'ng-qrcode';
import { InputTextComponent } from '../../controls/input-text/input-text.component';
import { ButtonComponent } from '../../controls/buttons/button/button.component';
//#endregion imports

@Component({
    selector: 'app-setup-totp',
    templateUrl: './setup-totp.component.html',
    styleUrls: ['./setup-totp.component.scss'],
    imports: [
    MessageComponent,
    ReactiveFormsModule,
    QrCodeComponent,
    InputTextComponent,
    ButtonComponent
],
})
export class SetupTotpComponent implements OnInit {
	private router = inject(Router);
	private loginService = inject(VMOnlineLoginService);
	private appSettingService = inject(AppSettingService);
	private authService = inject(AuthService);

	@HostBinding('className') componentClass: string;

	// implementing the host listener to listen to Enter key since the
	//  type="submit" does not seem to work
	@HostListener('window:keydown', ['$event'])
	handleKeyDown(event: KeyboardEvent) {
		// keyCode is deprecated. Now the ES6 preferred way of identifying the key press is .key
		if (event.key === 'Enter' && this.setupTotpForm?.status === 'VALID') {
			this.verify();
		}
	}

	//#region component declarations
	setupTotpForm: FormGroup<{ oneTimeCode: FormControl<string> }>;
	get cognitoUser(): CognitoUserSession {
		return this.loginService.cognitoUser;
	}
	get setupTotpCode(): string {
		return this.loginService.setupTotpCode;
	}
	qrCode: string = null;
	messages: Message[];
	isProcessing = false;
	readonly challengeTOTP = 'SOFTWARE_TOKEN_MFA';
	readonly preferredMFA = 'NOMFA';
	readonly maxLength = 7;

	//#endregion component declarations

	//#region constructor
	constructor() {
		this.componentClass = 'main-container';

		this.setupTotpForm = new FormGroup<{ oneTimeCode: FormControl<string> }>({
			oneTimeCode: new FormControl<string>(null, [Validators.required, Validators.maxLength(this.maxLength)]),
		});
	}
	//#endregion constructor

	ngOnInit(): void {
		if (!this.cognitoUser) {
			this.router.navigate([LocalRoutes.LOGIN]);
			return;
		}

		this.qrCode = this.getQrCode();
	}

	private getQrCode(): string {
		const setupTotpCode = this.loginService.setupTotpCode;
		const environmentId = this.appSettingService.appSetting.environmentId;
		const username = encodeURIComponent(this.cognitoUser.username);

		let issuer = 'VM%20Online';
		if (environmentId != 'live') {
			issuer += `%20${environmentId}`;
		}

		return `otpauth://totp/${issuer}:${username}?secret=${setupTotpCode}&issuer=${issuer}`;
	}

	loginFail(message?: string): void {
		if (!message) {
			message = `Something went wrong in setup process.
			Please contact support or try again later.`;
		}
		this.messages = [];
		this.isProcessing = false;
		this.messages.push({
			severity: 'error',
			summary: null,
			detail: message,
		});
	}

	verify(): void {
		const oneTimeCode = this.setupTotpForm.controls.oneTimeCode.value.replace(/[^0-9]/g, ''); // Strip anything not a number (users often enter bonus spaces)
		this.isProcessing = true;

		// congnito user pool setting
		this.authService.configureAmplify();

		Auth.verifyTotpToken(this.cognitoUser, oneTimeCode).then(
			() => {
				Auth.setPreferredMFA(this.cognitoUser, 'TOTP').then(
					() => {
						// After the successful setup of TOTP
						this.loginService.signInWithCognitoUser(this.loginService.cognitoUser).subscribe(
							(result) => {
								if (result.error) {
									this.loginFail(`Something went wrong in the setup process.
									Please contact support and quote error code
									${errorCodes.SIGN_IN_AFTER_TOTP_SETUP_FAILED},
									or try again later.`);
									return;
								}

								this.isProcessing = false;
							},
							() => {
								this.loginFail(`Something went wrong in the setup process.
								Please contact support and quote error code
								${errorCodes.SIGN_IN_AFTER_TOTP_SETUP_FAILED_HTTP},
								or try again later.`);
							}
						);
					},
					() => {
						this.loginFail(`Something went wrong in the setup process.
						Please contact support and quote error code
						${errorCodes.SET_PREFERRED_MFA_FAILED},
						or try again later.`);
					}
				);
			},
			(error) => {
				// if the user session has expired, there is no point keeping
				// the user on authenticator screen since user has to Login again.
				if (error?.code === 'NotAuthorizedException') {
					this.loginFail(`Your session has expired.
					We are redirecting you to the login page`);

					setTimeout(() => {
						this.router.navigate([LocalRoutes.LOGIN]);
					}, 3000);
				} else if (
					error?.code === 'EnableSoftwareTokenMFAException' ||
					error?.code === 'InvalidParameterException' ||
					error?.code === 'CodeMismatchException'
				) {
					this.loginFail(`The code you have entered is incorrect.
					If this problem persists, try re-scanning the QR code and replacing the account
					on your authentication app.`);
				} else {
					this.loginFail(`Something went wrong in the setup process.
					Please contact support and quote error code
					${errorCodes.VERIFY_TOTP_UNKNOWN_FAILURE},
					or try again later.`);
				}
			}
		);
	}

	copyToClipboard(): void {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(copy as any)(this.loginService.setupTotpCode);
	}
}
